import { useState } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';

import { fetchOrganizationCandidateTemplate, patchOrganization } from 'api';
import { Organization, AssociatedEntity } from 'components/Labels';
import { countryOptions, downloadBlobAsFile } from 'helpers';
import { useSnackContext } from 'hooks/useSnackContext';
import { OrganizationVerboseProps } from 'types';

import { AssociatedEdit } from './AssociatedEdit';
import { OrganizationEdit } from './OrganizationEdit/OrganizationEdit';
export type OrganizationEditFormProps = {
  organization: OrganizationVerboseProps;
  setOrganization: React.Dispatch<
    React.SetStateAction<OrganizationVerboseProps>
  >;
};

export const OrganizationEditForm = (props: OrganizationEditFormProps) => {
  const { organization, setOrganization } = props;
  const {
    name,
    country,
    id,
    // children,
    // parent,
    // candidates,
    // users,
    // parent,
    parent_id,
  } = organization;
  const [savingCountry, setSavingCountry] = useState(false);
  const [fetchingCandidateTemplate, setFetchingCandidateTemplate] =
    useState(false);

  const fetchEmployerTemplate = () => {
    const getTemplate = async () => {
      setFetchingCandidateTemplate(true);
      try {
        const { data, headers } = await fetchOrganizationCandidateTemplate(id);
        downloadBlobAsFile({ headers: headers, data: data });
      } catch (e) {
        console.log(e);
        setFetchingCandidateTemplate(false);
      } finally {
        setFetchingCandidateTemplate(false);
      }
    };
    getTemplate();
  };
  const { setSnack } = useSnackContext();
  const setCountry = async (countryId: string) => {
    setSavingCountry(true);
    try {
      const { data } = await patchOrganization(id, {
        country: countryId,
      });
      setOrganization(data);
      setSnack((props) => ({
        ...props,
        severity: 'success',
        open: true,
        message: 'Updated Organization Country',
      }));
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: 'Failed To Update Organization Country',
        error: e as Error,
      }));
      setSavingCountry(false);
    }
    setSavingCountry(false);
  };

  return (
    <Grid container>
      <Grid item xs={9}>
        <Typography variant='h4'>{name} </Typography>
        {parent_id ? <AssociatedEntity /> : <Organization />}{' '}
      </Grid>
      <Grid item xs={3}>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <LoadingButton
            loading={fetchingCandidateTemplate}
            sx={{ mr: 1 }}
            onClick={fetchEmployerTemplate}
          >
            Fetch Candidate Template
          </LoadingButton>
          <Autocomplete
            groupBy={(option) =>
              ['AF', 'US'].includes(option.value) ? '' : '--'
            }
            renderGroup={(params) => (
              <>
                <Divider key={params.key} />
                <Box>{params.children}</Box>
              </>
            )}
            loading={savingCountry}
            id='country-select'
            options={countryOptions}
            isOptionEqualToValue={(option, value) => {
              if (value.value === '') {
                return true;
              }
              return option.value === value.value;
            }}
            value={
              countryOptions.find(
                (countryOption) => countryOption.value === country,
              ) || { value: '', label: '' }
            }
            autoHighlight
            onChange={(_, option) => {
              option ? setCountry(option.value) : setCountry('');
            }}
            getOptionLabel={(option) => option.label}
            renderOption={(props, option) => (
              <Box
                component='li'
                key={option.value}
                sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                {...props}
              >
                <img
                  loading='lazy'
                  width='20'
                  src={`https://flagcdn.com/w20/${option.value.toLowerCase()}.png`}
                  srcSet={`https://flagcdn.com/w40/${option.value.toLowerCase()}.png 2x`}
                  alt=''
                />
                {option.label} ({option.value})
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size='small'
                label='Location'
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'select country', // disable autocomplete and autofill
                }}
              />
            )}
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ mb: 2 }} />
      </Grid>
      {parent_id ? (
        <AssociatedEdit
          setOrganization={setOrganization}
          organization={organization}
        />
      ) : (
        <OrganizationEdit
          setOrganization={setOrganization}
          organization={organization}
        />
      )}
    </Grid>
  );
};
